import { Injectable, OnDestroy, inject } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AsyncSubject,
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  map,
  Subject,
  takeUntil,
} from 'rxjs';
import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionStatus,
  InteractionType,
} from '@azure/msal-browser';
import { BD_FRONTEND_APP_CONFIG, BdAppEnvironmentService } from '@bindoc-projects/platform-lib/data';

/** Local session state only; API access still requires a token and server authorization. */
export type BdAuthenticationState =
  | { status: 'initializing'; account: null }
  | { status: 'anonymous'; account: null }
  | { status: 'authenticated'; account: AccountInfo }
  | { status: 'error'; account: null; error: unknown };

@Injectable({ providedIn: 'root' })
export class BdAuthenticationService implements OnDestroy {
  private readonly _msalBroadcastService = inject(MsalBroadcastService);
  private readonly _environmentService = inject(BdAppEnvironmentService);
  private readonly _msalService = inject(MsalService);
  private readonly _config = inject(BD_FRONTEND_APP_CONFIG);
  private readonly _destroying$ = new Subject<void>();
  private readonly _state = new BehaviorSubject<BdAuthenticationState>({
    status: 'initializing',
    account: null,
  });

  public readonly state$ = this._state.asObservable();
  public readonly msalInitialized = new AsyncSubject<void>();
  // Preserve the boolean API for consumers, but do not report logout during startup.
  public readonly authentication$ = this.state$.pipe(
    filter((state) => state.status !== 'initializing'),
    map((state) => state.status === 'authenticated'),
    distinctUntilChanged(),
  );
  public readonly accountInfo$ = this.state$.pipe(
    filter((state) => state.status !== 'initializing'),
    map((state) => state.account),
    distinctUntilChanged(),
  );

  public get state(): BdAuthenticationState {
    return this._state.value;
  }

  constructor() {
    this._msalBroadcastService.msalSubject$
      .pipe(takeUntil(this._destroying$))
      .subscribe((message) => this._handleEvent(message));
    this._msalBroadcastService.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        if (this.state.status !== 'error') this.refreshAccount();
        this.msalInitialized.next();
        this.msalInitialized.complete();
      });
  }

  public getPasswordResetPolicy(): { authority: string } {
    return {
      authority: this._environmentService.load(
        'AUTHENTICATION_PASSWORD_RESET_AUTHORITY',
      ),
    };
  }

  /** All account selection and state publication go through this service. */
  public selectAccount(account: AccountInfo): void {
    try {
      if (!this._isSignInAccount(account)) return;
      const active = this._msalService.instance.getActiveAccount();
      if (
        active?.homeAccountId !== account.homeAccountId ||
        active?.localAccountId !== account.localAccountId ||
        active?.tenantId !== account.tenantId
      ) {
        this._msalService.instance.setActiveAccount(account);
      }
      this._state.next({ status: 'authenticated', account });
    } catch (error) {
      this.reportError(error);
    }
  }

  public refreshAccount(): void {
    try {
      const instance = this._msalService.instance;
      const accounts = instance
        .getAllAccounts()
        .filter((account) => this._isSignInAccount(account));
      const active = instance.getActiveAccount();
      const account =
        accounts.find(
          (candidate) =>
            candidate.homeAccountId === active?.homeAccountId &&
            candidate.localAccountId === active?.localAccountId &&
            candidate.tenantId === active?.tenantId,
        ) ?? accounts[0];
      if (account) {
        this.selectAccount(account);
      } else {
        if (active) instance.setActiveAccount(null);
        this._state.next({ status: 'anonymous', account: null });
      }
    } catch (error) {
      this.reportError(error);
    }
  }

  public reportError(error: unknown): void {
    this._state.next({ status: 'error', account: null, error });
  }

  private _isSignInAccount(account: AccountInfo): boolean {
    const policy = this._getSignInPolicy().toLowerCase();
    const claims = account.idTokenClaims;
    return (
      claims?.acr?.toLowerCase() === policy ||
      claims?.tfp?.toLowerCase() === policy
    );
  }

  private _getSignInPolicy(): string {
    const msalConfig = this._config.msalConfig;
    const configuredName = msalConfig.policies?.names?.signUpSignIn?.trim();
    if (configuredName) return configuredName;

    // Older frontend configs (including meta-app's local assets/data.json)
    // only carry the B2C policy in the authority: /tenant/policy.
    const authority = new URL(
      msalConfig.publicClientApplication.auth.authority,
    );
    const segments = authority.pathname.split('/').filter(Boolean);
    const policy = segments.length >= 2 ? segments.at(-1) : undefined;
    if (!policy)
      throw new Error('Missing sign-in policy in authentication configuration');
    return policy;
  }

  private _handleEvent(message: EventMessage): void {
    switch (message.eventType) {
      case EventType.LOGIN_SUCCESS: {
        // MSAL v5 sends AccountInfo for login events.
        const account = message.payload as AccountInfo | null;
        if (account?.homeAccountId) this.selectAccount(account);
        break;
      }
      case EventType.ACQUIRE_TOKEN_SUCCESS: {
        const result = message.payload as AuthenticationResult | null;
        // Background token acquisition must not switch the user's active account.
        if (result?.account && this.state.status !== 'authenticated')
          this.selectAccount(result.account);
        break;
      }
      case EventType.ACQUIRE_TOKEN_FAILURE:
        if (
          message.interactionType === InteractionType.Redirect ||
          message.interactionType === InteractionType.Popup
        ) {
          this.reportError(message.error);
        }
        break;
      case EventType.LOGOUT_SUCCESS:
      case EventType.ACTIVE_ACCOUNT_CHANGED:
        this.refreshAccount();
        break;
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }
}
